// src/pages/UpcomingBookings.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API_CONFIG, HOTELS, MEAL_PLANS } from '../App';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Pagination, Modal } from 'react-bootstrap';
import './UpcomingBookings.css';

function UpcomingBookings() {
  const { loginUser, userId } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHotel, setFilterHotel] = useState('');
  const [filterMeal, setFilterMeal] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [viewMode, setViewMode] = useState(window.innerWidth <= 768 ? 'card' : 'table');
  const itemsPerPage = 10;

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);
  
  // Room types for editing
  const ALL_ROOM_TYPES = [
    { id: '4bed', label: '4 Bed', order: 1 },
    { id: '5bed', label: '5 Bed', order: 2 },
    { id: '6bed', label: '6 Bed', order: 3 }
  ];

  const ROOM_COUNTS = [
    { value: 1, label: '1 Room' },
    { value: 2, label: '2 Rooms' },
    { value: 3, label: '3 Rooms' },
    { value: 4, label: '4 Rooms' },
    { value: 5, label: '5 Rooms' },
    { value: 6, label: '6 Rooms' },
    { value: 7, label: '7 Rooms' },
    { value: 8, label: '8 Rooms' },
    { value: 9, label: '9 Rooms' },
    { value: 10, label: '10 Rooms' }
  ];
  
  const PAYMENT_PERSONS = [
    { id: 1, name: 'Shivpuri Account' },
    { id: 2, name: 'Rahul Bagwari' },
    { id: 3, name: 'Kuldeep Bagwari' },
    { id: 4, name: 'Bhagat Singh Bagwari' }
  ];

  // Edit form state
  const [editData, setEditData] = useState({
    id: null,
    hotel: '',
    username: '',
    email: '',
    phone: '',
    mealPlan: 'EP',
    checkInDate: '',
    days: 1,
    numberOfPersons: 1,
    rooms: [],
    paymentPerson: 1,
    totalAmount: '',
    tokenAmount: '',
    balanceAmount: 0,
    specialRequest: ''
  });
  
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentPersonId, setPaymentPersonId] = useState(1);
  const [paymentNote, setPaymentNote] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setViewMode(window.innerWidth <= 768 ? 'card' : 'table');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchUpcomingBookings();
  }, []);

  const fetchUpcomingBookings = async (page = currentPage) => {
    setLoading(true);
    try {
      const response = await fetch(API_CONFIG.UPCOMING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify({
          userId: parseInt(userId) || null,
          search: searchTerm || undefined,
          hotel: filterHotel || undefined,
          meal_plan: filterMeal || undefined,
          date_from: filterDateFrom || undefined,
          date_to: filterDateTo || undefined,
          page: page,
          per_page: itemsPerPage
        })
      });
      const data = await response.json();
      if (data.status === 'success' || data.success) {
        const bookingData = data.data || [];
        setBookings(bookingData);
        if (data.pagination) {
          setTotalPages(data.pagination.last_page || 1);
          setTotalItems(data.pagination.total || 0);
          setCurrentPage(data.pagination.current_page || 1);
        }
      }
    } catch (error) {
      console.error('Error fetching upcoming bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      fetchUpcomingBookings(1);
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, filterHotel, filterMeal, filterDateFrom, filterDateTo]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchUpcomingBookings(newPage);
    }
  };

  const totalAmount = bookings.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
  const totalBalance = bookings.reduce((sum, b) => sum + (parseFloat(b.balance_amount) || 0), 0);

  const handleViewBooking = async (booking) => {
    try {
      const response = await fetch(API_CONFIG.VIEW_BOOKING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify({ 
          id: booking.id,
          userId: parseInt(userId) || null
        })
      });
      const data = await response.json();
      if (data.status === 'success' || data.success) {
        setSelectedBooking(data.data);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      alert('Failed to fetch booking details. Please try again.');
    }
  };

  // Get selected room types for edit
  const getSelectedRoomTypes = () => {
    return editData.rooms.map(room => room.roomType);
  };

  // Get available room types for edit
  const getAvailableRoomTypes = (currentRoomId) => {
    const selectedOtherTypes = editData.rooms
      .filter(room => room.id !== currentRoomId)
      .map(room => room.roomType);
    return ALL_ROOM_TYPES.filter(type => !selectedOtherTypes.includes(type.id));
  };

  // Check if all room types are selected in edit
  const allRoomTypesSelected = getSelectedRoomTypes().length >= ALL_ROOM_TYPES.length;

  // Calculate total rooms in edit
  const totalEditRooms = editData.rooms.reduce((sum, room) => sum + parseInt(room.numberOfRooms || 1), 0);

  // Add Room in edit
  const addEditRoom = () => {
    const availableTypes = ALL_ROOM_TYPES.filter(type => !getSelectedRoomTypes().includes(type.id));
    if (availableTypes.length > 0) {
      const nextType = availableTypes[0];
      setEditData(prev => ({
        ...prev,
        rooms: [...prev.rooms, { 
          id: Date.now(), 
          roomType: nextType.id, 
          numberOfRooms: 1 
        }]
      }));
    }
  };

  // Remove Room in edit
  const removeEditRoom = (roomId) => {
    if (editData.rooms.length > 1) {
      const newRooms = editData.rooms.filter(room => room.id !== roomId);
      const sortedRooms = newRooms.sort((a, b) => {
        const orderA = ALL_ROOM_TYPES.find(t => t.id === a.roomType)?.order || 0;
        const orderB = ALL_ROOM_TYPES.find(t => t.id === b.roomType)?.order || 0;
        return orderA - orderB;
      });
      setEditData(prev => ({ ...prev, rooms: sortedRooms }));
    }
  };

  // Update Room Type in edit
  const updateEditRoomType = (roomId, value) => {
    const isTypeSelected = editData.rooms.some(room => room.id !== roomId && room.roomType === value);
    
    if (isTypeSelected) {
      alert('This room type is already selected. Please choose a different type.');
      return;
    }

    setEditData(prev => ({
      ...prev,
      rooms: prev.rooms.map(room => {
        if (room.id === roomId) {
          return { ...room, roomType: value };
        }
        return room;
      })
    }));
  };

  // Update Room Count in edit
  const updateEditRoomCount = (roomId, value) => {
    setEditData(prev => ({
      ...prev,
      rooms: prev.rooms.map(room => {
        if (room.id === roomId) {
          return { ...room, numberOfRooms: parseInt(value) };
        }
        return room;
      })
    }));
  };

  const handleEditBooking = (booking) => {
    // Parse rooms from booking data
    let rooms = [];
    if (booking.rooms && booking.rooms.length > 0) {
      // Group rooms by type and count
      const roomMap = {};
      booking.rooms.forEach(room => {
        const type = room.roomtype || room.roomType || '4bed';
        if (roomMap[type]) {
          roomMap[type].count += 1;
        } else {
          roomMap[type] = { type, count: 1 };
        }
      });
      
      rooms = Object.keys(roomMap).map((type, index) => ({
        id: Date.now() + index,
        roomType: type,
        numberOfRooms: roomMap[type].count
      }));
    } else {
      rooms = [{ id: Date.now(), roomType: '4bed', numberOfRooms: 1 }];
    }

    setEditData({
      id: booking.id,
      hotel: booking.hotel_id || booking.hotel,
      username: booking.username,
      email: booking.email,
      phone: booking.phone,
      mealPlan: booking.meal_plan || 'EP',
      checkInDate: booking.check_in_date,
      days: booking.number_of_days || 1,
      numberOfPersons: booking.number_of_persons || 1,
      rooms: rooms,
      paymentPerson: booking.payment_person || 1,
      totalAmount: booking.total_amount || '',
      tokenAmount: booking.token_amount || '',
      balanceAmount: booking.balance_amount || 0,
      specialRequest: booking.special_request || ''
    });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'totalAmount' || field === 'tokenAmount') {
      const total = parseFloat(field === 'totalAmount' ? value : editData.totalAmount) || 0;
      const token = parseFloat(field === 'tokenAmount' ? value : editData.tokenAmount) || 0;
      setEditData(prev => ({ ...prev, balanceAmount: total - token }));
    }
  };

  const handleEditSubmit = async () => {
    // Build room details for API
    const roomDetails = editData.rooms.map((room) => ({
      roomType: room.roomType,
      count: parseInt(room.numberOfRooms) || 1
    }));

    const bookingData = {
      id: editData.id,
      userId: parseInt(userId) || null,
      hotelId: parseInt(editData.hotel),
      hotelName: HOTELS.find(h => h.id === parseInt(editData.hotel))?.name || '',
      username: editData.username,
      email: editData.email,
      phone: editData.phone,
      mealPlan: editData.mealPlan,
      checkInDate: editData.checkInDate,
      checkInTime: null,
      numberOfDays: parseInt(editData.days),
      numberOfRooms: totalEditRooms,
      numberOfPersons: parseInt(editData.numberOfPersons),
      rooms: roomDetails,
      specialRequest: editData.specialRequest || 'None',
      paymentPerson: parseInt(editData.paymentPerson),
      totalAmount: parseFloat(editData.totalAmount) || 0,
      tokenAmount: parseFloat(editData.tokenAmount) || 0,
      balanceAmount: editData.balanceAmount,
      status: 'confirmed',
      createdBy: loginUser
    };

    try {
      const response = await fetch(API_CONFIG.UPDATE_BOOKING_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();
      if (response.ok && data.status === 'success') {
        alert('✅ Booking updated successfully!');
        setShowEditModal(false);
        fetchUpcomingBookings();
      } else {
        throw new Error(data.message || 'Update failed');
      }
    } catch (error) {
      alert(`❌ Update failed!\n\nError: ${error.message}`);
    }
  };

  const handlePaymentClick = (booking) => {
    setPaymentBooking(booking);
    setPaymentPersonId(booking.payment_person || 1);
    setPaymentAmount('');
    setPaymentNote('');
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (parseFloat(paymentAmount) > parseFloat(paymentBooking.balance_amount || 0)) {
      alert(`Payment amount cannot exceed balance amount: ₹${paymentBooking.balance_amount || 0}`);
      return;
    }

    const paymentData = {
      bookingId: paymentBooking.id,
      userId: parseInt(userId) || null,
      amount: parseFloat(paymentAmount),
      paymentPerson: paymentPersonId,
      paymentPersonName: PAYMENT_PERSONS.find(p => p.id === paymentPersonId)?.name || 'Unknown',
      note: paymentNote || '',
      paymentDate: new Date().toISOString().split('T')[0]
    };

    try {
      const response = await fetch(API_CONFIG.PAYMENT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(paymentData)
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        alert(`✅ Payment recorded successfully!\n\nAmount: ₹${paymentAmount}\nPerson: ${paymentData.paymentPersonName}`);
        setShowPaymentModal(false);
        fetchUpcomingBookings();
      } else {
        throw new Error(data.message || 'Payment failed');
      }
    } catch (error) {
      alert(`❌ Payment failed!\n\nError: ${error.message}`);
    }
  };

  const renderCardView = () => {
    return (
      <Row className="g-3">
        {bookings.length === 0 ? (
          <Col xs={12}>
            <div className="text-center py-5 text-muted">No upcoming bookings</div>
          </Col>
        ) : (
          bookings.map((booking, index) => (
            <Col xs={12} md={6} lg={4} key={booking.id}>
              <Card className="h-100 card-shadow border-0 rounded-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Badge bg="primary" className="rounded-pill px-3 py-2">
                      #{((currentPage - 1) * itemsPerPage) + index + 1}
                    </Badge>
                    <div className="d-flex gap-1">
                      <Button variant="primary" size="sm" className="rounded-3" onClick={() => handleViewBooking(booking)}>👁️</Button>
                      <Button variant="warning" size="sm" className="rounded-3" onClick={() => handleEditBooking(booking)}>✏️</Button>
                      <Button variant="success" size="sm" className="rounded-3" onClick={() => handlePaymentClick(booking)}>💰</Button>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <strong>{booking.username}</strong>
                    <br />
                    <small className="text-muted">{booking.phone}</small>
                  </div>
                  
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Hotel:</span>
                    <span className="fw-semibold">{booking.hotel}</span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Check-in:</span>
                    <span className="fw-semibold">{booking.check_in_date}</span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Rooms:</span>
                    <span className="fw-semibold">{booking.room_details || 'N/A'}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Total:</span>
                    <span className="fw-bold text-success">₹{booking.total_amount || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Token:</span>
                    <span className="fw-bold text-primary">₹{booking.token_amount || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between small">
                    <span className="text-muted">Balance:</span>
                    <span className="fw-bold text-danger">₹{booking.balance_amount || 0}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>
    );
  };

  const renderTableView = () => {
    return (
      <div className="table-responsive">
        <Table hover className="rounded-3 overflow-hidden">
          <thead className="bg-primary-dark text-white">
            <tr>
              <th>#</th>
              <th>Guest</th>
              <th>Hotel</th>
              <th>Check-in</th>
              <th>Rooms</th>
              <th>Total</th>
              <th>Token</th>
              <th>Balance</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">No upcoming bookings</td>
              </tr>
            ) : (
              bookings.map((booking, index) => (
                <tr key={booking.id}>
                  <td>{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                  <td>
                    <strong>{booking.username}</strong>
                    <br />
                    <small className="text-muted">{booking.phone}</small>
                  </td>
                  <td>{booking.hotel}</td>
                  <td>{booking.check_in_date}</td>
                  <td>
                    <Badge bg="secondary" className="rounded-pill">
                      {booking.room_details || 'N/A'}
                    </Badge>
                  </td>
                  <td className="fw-bold text-success">₹{booking.total_amount || 0}</td>
                  <td className="fw-bold text-primary">₹{booking.token_amount || 0}</td>
                  <td className="fw-bold text-danger">₹{booking.balance_amount || 0}</td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <Button variant="primary" size="sm" className="rounded-3" onClick={() => handleViewBooking(booking)}>👁️</Button>
                      <Button variant="warning" size="sm" className="rounded-3" onClick={() => handleEditBooking(booking)}>✏️</Button>
                      <Button variant="success" size="sm" className="rounded-3" onClick={() => handlePaymentClick(booking)}>💰</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Container fluid className="upcoming-bookings-page py-3 fade-in">
      <Row className="mb-4">
        <Col xs={12} md={6}>
          <h2 className="fw-bold text-primary-dark">📅 Upcoming Bookings</h2>
        </Col>
        <Col xs={12} md={6} className="text-md-end">
          <div className="d-flex flex-wrap gap-2 justify-content-md-end">
            <Badge bg="primary" className="rounded-pill px-3 py-2 fs-6">
              Total: ₹{totalAmount.toLocaleString()}
            </Badge>
            <Badge bg="danger" className="rounded-pill px-3 py-2 fs-6">
              Balance: ₹{totalBalance.toLocaleString()}
            </Badge>
            <Badge bg="info" className="rounded-pill px-3 py-2 fs-6">
              {totalItems} bookings
            </Badge>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Control
            type="text"
            placeholder="Search by name, phone, reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-3"
          />
        </Col>
        <Col xs={6} md={3}>
          <Form.Select
            value={filterHotel}
            onChange={(e) => setFilterHotel(e.target.value)}
            className="rounded-3"
          >
            <option value="">All Hotels</option>
            {HOTELS.map(h => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6} md={3}>
          <Form.Select
            value={filterMeal}
            onChange={(e) => setFilterMeal(e.target.value)}
            className="rounded-3"
          >
            <option value="">All Meals</option>
            {Object.entries(MEAL_PLANS).map(([key, plan]) => (
              <option key={key} value={key}>{plan.label}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={12} md={2}>
          <Button variant="danger" className="w-100 rounded-3" onClick={() => {
            setSearchTerm('');
            setFilterHotel('');
            setFilterMeal('');
            setFilterDateFrom('');
            setFilterDateTo('');
            setCurrentPage(1);
            fetchUpcomingBookings(1);
          }}>
            ✕ Clear
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={6} md={4}>
          <Form.Control
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="rounded-3"
            placeholder="From"
          />
        </Col>
        <Col xs={6} md={4}>
          <Form.Control
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="rounded-3"
            placeholder="To"
          />
        </Col>
        <Col xs={12} md={4} className="text-end">
          <div className="d-flex gap-2 justify-content-md-end">
            <Button 
              variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
              className="rounded-3"
              onClick={() => setViewMode('table')}
            >
              📋 Table
            </Button>
            <Button 
              variant={viewMode === 'card' ? 'primary' : 'outline-secondary'}
              className="rounded-3"
              onClick={() => setViewMode('card')}
            >
              🃏 Cards
            </Button>
          </div>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading...</div>
      ) : (
        <>
          {viewMode === 'table' ? renderTableView() : renderCardView()}

          {totalPages > 1 && (
            <Row className="mt-4">
              <Col xs={12} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="text-muted">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                </span>
                <Pagination>
                  <Pagination.Prev 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && <Pagination.Item onClick={() => handlePageChange(currentPage + 1)}>{currentPage + 1}</Pagination.Item>}
                  {totalPages > 2 && <Pagination.Ellipsis />}
                  {totalPages > 1 && <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>}
                  <Pagination.Next 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      )}

      {/* View Booking Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom border-warning">
          <Modal.Title className="fw-bold text-primary-dark">📋 Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <Row className="g-2">
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Reference:</span> <span className="fw-semibold text-warning">{selectedBooking.booking_reference}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Guest:</span> <span className="fw-semibold">{selectedBooking.username}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Email:</span> <span className="fw-semibold">{selectedBooking.email}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Phone:</span> <span className="fw-semibold">{selectedBooking.phone}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Hotel:</span> <span className="fw-semibold">{selectedBooking.hotel}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Meal Plan:</span> <span className="fw-semibold">{selectedBooking.meal_plan}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Check-in:</span> <span className="fw-semibold">{selectedBooking.check_in_date}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Days:</span> <span className="fw-semibold">{selectedBooking.number_of_days}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Rooms:</span> <span className="fw-semibold">{selectedBooking.number_of_rooms}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Persons:</span> <span className="fw-semibold">{selectedBooking.number_of_persons}</span></div>
              </Col>
              
              {selectedBooking.rooms && selectedBooking.rooms.length > 0 && (
                <Col xs={12}>
                  <div className="mb-2">
                    <span className="text-muted small fw-bold">Room Details:</span>
                    <div className="bg-light p-2 rounded-3 mt-1">
                      {selectedBooking.rooms.map((room, idx) => (
                        <div key={idx} className="small">Room {idx + 1}: <strong>{room.roomtype || 'Unknown'}</strong> - {room.person} person{room.person > 1 ? 's' : ''}</div>
                      ))}
                    </div>
                  </div>
                </Col>
              )}

              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Payment Person:</span> <span className="fw-semibold">{selectedBooking.payment_person_name || 'N/A'}</span></div>
              </Col>
              <Col xs={12} md={6}>
                <div className="mb-2"><span className="text-muted small fw-bold">Status:</span> <Badge bg={selectedBooking.status === 'confirmed' ? 'success' : 'warning'} className="rounded-pill">{selectedBooking.status || 'Confirmed'}</Badge></div>
              </Col>
              <Col xs={12} md={4}>
                <div className="mb-2"><span className="text-muted small fw-bold">Total Amount:</span> <span className="fw-bold text-success">₹{selectedBooking.total_amount || 0}</span></div>
              </Col>
              <Col xs={12} md={4}>
                <div className="mb-2"><span className="text-muted small fw-bold">Token Paid:</span> <span className="fw-bold text-primary">₹{selectedBooking.token_amount || 0}</span></div>
              </Col>
              <Col xs={12} md={4}>
                <div className="mb-2"><span className="text-muted small fw-bold">Balance:</span> <span className="fw-bold text-danger">₹{selectedBooking.balance_amount || 0}</span></div>
              </Col>
              
              {selectedBooking.payments && selectedBooking.payments.length > 0 && (
                <Col xs={12}>
                  <div className="mb-2">
                    <span className="text-muted small fw-bold">Payment History:</span>
                    <div className="table-responsive mt-1">
                      <Table size="sm" className="bg-light rounded-3">
                        <thead className="bg-dark text-white">
                          <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Person</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBooking.payments.map((payment, idx) => (
                            <tr key={idx}>
                              <td>{payment.payment_date}</td>
                              <td className="fw-bold text-success">₹{payment.amount}</td>
                              <td>{payment.payment_person_name || 'N/A'}</td>
                              <td>{payment.note || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              )}

              <Col xs={12}>
                <div className="mb-2"><span className="text-muted small fw-bold">Special Request:</span> <span className="fw-semibold">{selectedBooking.special_request || 'None'}</span></div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded-3" onClick={() => setShowViewModal(false)}>Close</Button>
          {selectedBooking && (
            <>
              <Button variant="warning" className="rounded-3" onClick={() => {
                setShowViewModal(false);
                handleEditBooking(selectedBooking);
              }}>✏️ Edit</Button>
              <Button variant="success" className="rounded-3" onClick={() => {
                setShowViewModal(false);
                handlePaymentClick(selectedBooking);
              }}>💰 Payment</Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Booking Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom border-warning">
          <Modal.Title className="fw-bold text-primary-dark">✏️ Edit Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Hotel <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={editData.hotel}
                  onChange={(e) => handleEditChange('hotel', e.target.value)}
                  className="rounded-3"
                >
                  <option value="">Select Hotel</option>
                  {HOTELS.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Guest Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={editData.username}
                  onChange={(e) => handleEditChange('username', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Email <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  value={editData.email}
                  onChange={(e) => handleEditChange('email', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Phone <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => handleEditChange('phone', e.target.value.replace(/\D/g, ''))}
                  maxLength="10"
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Check-in <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={editData.checkInDate}
                  onChange={(e) => handleEditChange('checkInDate', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Days</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={editData.days}
                  onChange={(e) => handleEditChange('days', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Number of Persons <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={editData.numberOfPersons}
                  onChange={(e) => handleEditChange('numberOfPersons', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Meal Plan</Form.Label>
                <Form.Select
                  value={editData.mealPlan}
                  onChange={(e) => handleEditChange('mealPlan', e.target.value)}
                  className="rounded-3"
                >
                  {Object.entries(MEAL_PLANS).map(([key, plan]) => (
                    <option key={key} value={key}>{plan.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Payment Person</Form.Label>
                <Form.Select
                  value={editData.paymentPerson}
                  onChange={(e) => handleEditChange('paymentPerson', parseInt(e.target.value))}
                  className="rounded-3"
                >
                  {PAYMENT_PERSONS.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            {/* Rooms Section in Edit */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Room Details</Form.Label>
                <div className="rooms-section">
                  <div className="rooms-summary">
                    <Badge bg="primary" className="rounded-pill px-3 py-2">
                      Total Rooms: {totalEditRooms}
                    </Badge>
                    <Badge bg="info" className="rounded-pill px-3 py-2">
                      Selected Room Types: {editData.rooms.length}/{ALL_ROOM_TYPES.length}
                    </Badge>
                  </div>
                  
                  {editData.rooms.map((room, index) => {
                    const availableTypes = getAvailableRoomTypes(room.id);
                    
                    return (
                      <div key={room.id} className="room-row">
                        <span className="room-number">Room {index + 1}</span>
                        
                        <Form.Select
                          value={room.roomType}
                          onChange={(e) => updateEditRoomType(room.id, e.target.value)}
                          className="room-type-select rounded-3"
                        >
                          {ALL_ROOM_TYPES.map(type => (
                            <option 
                              key={type.id} 
                              value={type.id}
                              disabled={!availableTypes.some(t => t.id === type.id) && type.id !== room.roomType}
                            >
                              {type.label} {!availableTypes.some(t => t.id === type.id) && type.id !== room.roomType ? '(Already Selected)' : ''}
                            </option>
                          ))}
                        </Form.Select>
                        
                        <Form.Select
                          value={room.numberOfRooms}
                          onChange={(e) => updateEditRoomCount(room.id, e.target.value)}
                          className="room-count-select rounded-3"
                        >
                          {ROOM_COUNTS.map(count => (
                            <option key={count.value} value={count.value}>{count.label}</option>
                          ))}
                        </Form.Select>
                        
                        {editData.rooms.length > 1 && (
                          <Button
                            variant="danger"
                            size="sm"
                            className="btn-remove rounded-3"
                            onClick={() => removeEditRoom(room.id)}
                          >
                            ✕
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  
                  {!allRoomTypesSelected && (
                    <Button
                      variant="primary"
                      className="btn-add-room rounded-3"
                      onClick={addEditRoom}
                    >
                      + Add Room
                    </Button>
                  )}
                  
                  {allRoomTypesSelected && (
                    <div className="all-types-selected-message">
                      ✅ All room types have been selected
                    </div>
                  )}
                </div>
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Total Amount <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="100"
                  value={editData.totalAmount}
                  onChange={(e) => handleEditChange('totalAmount', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Token Amount</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="100"
                  value={editData.tokenAmount}
                  onChange={(e) => handleEditChange('tokenAmount', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Balance Amount</Form.Label>
                <Form.Control
                  type="text"
                  value={editData.balanceAmount}
                  readOnly
                  className="rounded-3 bg-light fw-bold"
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Special Request</Form.Label>
                <Form.Control
                  as="textarea"
                  rows="2"
                  value={editData.specialRequest}
                  onChange={(e) => handleEditChange('specialRequest', e.target.value)}
                  className="rounded-3"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded-3" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" className="rounded-3 btn-primary-dark" onClick={handleEditSubmit}>Update Booking</Button>
        </Modal.Footer>
      </Modal>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
        <Modal.Header closeButton className="border-bottom border-warning">
          <Modal.Title className="fw-bold text-primary-dark">💰 Add Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentBooking && (
            <>
              <div className="bg-light p-3 rounded-3 mb-3">
                <div className="small"><span className="fw-bold">Booking:</span> {paymentBooking.booking_reference}</div>
                <div className="small"><span className="fw-bold">Guest:</span> {paymentBooking.username}</div>
                <div className="small"><span className="fw-bold">Total:</span> ₹{paymentBooking.total_amount || 0}</div>
                <div className="small"><span className="fw-bold">Token Paid:</span> ₹{paymentBooking.token_amount || 0}</div>
                <div className="small"><span className="fw-bold text-danger">Balance:</span> ₹{paymentBooking.balance_amount || 0}</div>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Payment Amount *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max={paymentBooking.balance_amount || 0}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="rounded-3"
                  placeholder="Enter amount"
                />
                <Form.Text className="text-muted">Max: ₹{paymentBooking.balance_amount || 0}</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Payment Person *</Form.Label>
                <Form.Select
                  value={paymentPersonId}
                  onChange={(e) => setPaymentPersonId(parseInt(e.target.value))}
                  className="rounded-3"
                >
                  {PAYMENT_PERSONS.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className="fw-semibold">Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows="2"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  className="rounded-3"
                  placeholder="Payment note..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded-3" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
          <Button
            variant="success"
            className="rounded-3"
            onClick={handlePaymentSubmit}
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
          >
            💰 Record Payment
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default UpcomingBookings;